import {BaseQueryFn, FetchArgs, fetchBaseQuery, FetchBaseQueryError} from '@reduxjs/toolkit/query';
import {addBreadcrumb, captureException} from '@sentry/browser';
import flagsmith from 'flagsmith';

// eslint-disable-next-line no-restricted-imports
import {not, path} from 'ramda';
import {isNotNilOrEmpty, isString} from 'ramda-adjunct';

import {browserStorageKey} from '@dms/config';
import {getWorkspaceFromUri, redirectToLogin, redirectToWorkspace} from '@dms/routes';

import {BaseQueryExtraOptions, parseZodSchema, ParseZodSchemaQueryInfo} from 'shared/web';

import {publicApi} from '../modules/publicApi/publicApi';
import {RefreshTokenApiArg} from '../modules/publicApi/types';
import {isCI} from './constants/isCI';
import {isDev} from './constants/isDev';
import {handleClientNotification} from './utils/handleClientNotification';

const RETRY_DELAY = 2000;
const INFRASTRUCTURE_ERRORS_CODES = [502, 503, 504];

const getHeaderValue = (headers: FetchArgs['headers'], key: string): string | undefined => {
  if (headers instanceof Headers) {
    return headers.get(key) ?? undefined;
  }
  if (Array.isArray(headers)) {
    return headers.find(([k]) => k.toLowerCase() === key.toLowerCase())?.[1];
  }
  if (headers) {
    return headers[key] as string | undefined;
  }
  return undefined;
};

const getArgsWithAuthorization = (args: FetchArgs, branch?: string) => {
  const accessToken = sessionStorage.getItem(browserStorageKey.ACCESS_TOKEN);
  const {workspace} = getWorkspaceFromUri();

  const explicitBranch =
    getHeaderValue(args.headers, 'x-branch') ?? getHeaderValue(args.headers, 'X-Branch');
  const hasExplicitBranch = isNotNilOrEmpty(explicitBranch);

  return {
    ...args,
    headers: {
      ...args.headers,
      credentials: 'include',
      'accept-language': localStorage.getItem(browserStorageKey.LAST_KNOWN_LANGUAGE) || 'en',
      ...(accessToken ? {authorization: `Bearer ${accessToken}`} : {}),
      // TODO - Blocked by CORS on METADA
      // 'x-country-format': 'iso', // https://carvago.slack.com/archives/G01AN5XCXGX/p1634202539053900
      ...(!hasExplicitBranch && branch ? {'X-Branch': branch} : {}),
      ...(workspace ? {'X-Workspace': workspace} : {}),
    },
  };
};

const fetchBaseQueryInstance = fetchBaseQuery({baseUrl: process.env.METADA_URL});

export interface MetadaCustomFetchArgs extends FetchArgs {
  queryArg?: Record<string, unknown> | void;
}

export const metadaBaseQuery: BaseQueryFn<
  MetadaCustomFetchArgs,
  unknown,
  FetchBaseQueryError,
  BaseQueryExtraOptions
> = async (args, api, extraOptions) => {
  const activeBranchId = path<string>(['branch', 'activeBranchId'], api.getState());

  const queryInfo: Omit<ParseZodSchemaQueryInfo, 'schemaType'> = {
    method: args.method,
    endpointName: api.endpoint,
    url: args.url,
  };

  if (isDev && not(isCI) && extraOptions && extraOptions.requestSchema) {
    parseZodSchema({
      data: args.queryArg,
      schema: extraOptions.requestSchema,
      queryInfo: {
        ...queryInfo,
        schemaType: 'request',
      },
    });
  }

  let result = await fetchBaseQueryInstance(
    getArgsWithAuthorization(args, activeBranchId),
    api,
    extraOptions
  );

  if (isDev && not(isCI) && extraOptions && extraOptions.responseSchema && 'data' in result) {
    parseZodSchema({
      data: result.data,
      schema: extraOptions.responseSchema,
      queryInfo: {
        ...queryInfo,
        schemaType: 'response',
      },
    });
  }

  /**
   * This retry logic is used to handle infrastructure errors. This type of errors are often ad-hoc
   * and not related to the request payload, so retrying the request should help.
   * https://carvago.atlassian.net/browse/T20-80058
   */
  if (result.error) {
    if (
      INFRASTRUCTURE_ERRORS_CODES.includes(
        Number(result.meta?.response?.status ?? result.error.status)
      )
    ) {
      addBreadcrumb({
        category: 'api',
        message: `Retrying metada API request ${args.url}, because response status is ${result.meta?.response?.status ?? result.error.status}`,
        level: 'error',
      });
      await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY));
      result = await fetchBaseQueryInstance(
        getArgsWithAuthorization(args, activeBranchId),
        api,
        extraOptions
      );
    }
  }

  if (result.error && Number(result.error.status) >= 500) {
    captureException(result.error);
  }

  if (
    result.error &&
    (result.error.status === 401 ||
      (isString(result?.error?.data) && result?.error?.data.includes('authorization')) ||
      (isString(result?.error?.data) && result?.error?.data.includes('Unauthorized')))
  ) {
    const getRefreshToken = localStorage.getItem(browserStorageKey.REFRESH_TOKEN);
    const {workspace, shouldRedirectToAppWorkspace} = getWorkspaceFromUri();
    const currentUserEmail = path<string>(['user', 'email'], api.getState());

    if (!workspace || shouldRedirectToAppWorkspace) {
      redirectToWorkspace();
      return result;
    }

    if (!getRefreshToken) {
      redirectToLogin({currentUserEmail});
      return result;
    }

    const searchParams = new URLSearchParams(window.location.search);
    searchParams.delete('directLink');

    const refreshTokenArgs: RefreshTokenApiArg = {
      workspace,
      refreshTokenRequestBody: {
        refreshToken: getRefreshToken,
      },
    };

    await api
      .dispatch(publicApi.endpoints.refreshToken.initiate(refreshTokenArgs))
      .unwrap()
      .then(({refreshToken, token}) => {
        sessionStorage.setItem(browserStorageKey.ACCESS_TOKEN, token);
        localStorage.setItem(browserStorageKey.REFRESH_TOKEN, refreshToken);
      })
      .catch(() => {
        sessionStorage.removeItem(browserStorageKey.ACCESS_TOKEN);
        localStorage.removeItem(browserStorageKey.REFRESH_TOKEN);
        flagsmith.logout();

        redirectToLogin({currentUserEmail});
      });

    result = await fetchBaseQueryInstance(
      getArgsWithAuthorization(args, activeBranchId),
      api,
      extraOptions
    );
  }

  // Status 299 indicates an issue with the client's payload without blocking the flow.
  // Message from BE should be displayed to the client.
  if (result.meta?.response?.status === 299) {
    handleClientNotification(result.meta?.response?.headers);
  }

  return result;
};
